import { Autocomplete, Box, Grid, TextField, Typography } from '@mui/material'
import { LegalEntityParty } from '@types'
import {
  useContext,
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
  useMemo
} from 'react'
import { RootState, useDispatch, useSelector } from 'redux/store'
import { clearAllLegalEntites } from 'redux/slices/legalEntities'
import { clearAllTermsConditions } from 'redux/slices/terms'
import { GlobalInfoContext } from 'contexts/GlobalStateContext'
import { useSocketConnection } from 'hooks/useSocketConnection'
import Item from 'pages/dashboard/sub-sections/items'
import { PointOfContact } from './forms/types/PointOfContact'
import BusinessIdAutoComplete from './BusinessIdentity'
import PointOfContactEditor from './PointOfContactEditor'
import PointOfContactSummary from './PointOfContactSummary'
import TrusteeNameEditor from './TrusteeNameEditor'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { entityNeedsTrusteeName, getBeneficiaryDisplayName, getTrusteeName } from 'utils/trustEntityUtils'

export type LegalEntityCodeType = 'ABN' | undefined

export const legalEntityBaseValue = {
  commonName: '',
  onboarded: false,
  contactFirstName: '',
  contactLastName: '',
  email: '',
  phone: '',
  legalEntityIdentifier: '',
  legalEntityCodes: [
    {
      legalEntityCodeType: 'ABN' as LegalEntityCodeType,
      legalEntityCodeData: ''
    }
  ],
  trusteeName: '',
  legalEntityType: '',
  handlingInstructions: undefined
}

type EntityProps = {
  label: string
  type: string
  touched?: any
  errors?: any
  getFieldProps?: any
  setValue: (value: LegalEntityParty) => void
  optionalSelectedValue?: LegalEntityParty
  initialOption?: LegalEntityParty
  handleClear: (value) => void
  initiatedBy: string
  initialValues: LegalEntityParty[] | any
  resetTermsRef?: React.RefObject<any>
  resetIssuer?: React.RefObject<any>
  issuerRef?: React.RefObject<any>
  clearable?: boolean
  singleEntry?: boolean
  setPOC?: (value?: PointOfContact | undefined) => void
  disabled?: boolean
  isOnbordedRequired?: boolean
  /** Callback when trustee name changes for trust beneficiaries */
  onTrusteeNameChange?: (trusteeName: string) => void
}

/**
 * Helper function to check if an entity should show the POC form.
 * Centralizes the logic so it's consistent across render and state updates.
 */
const shouldShowPocForEntity = (entity: LegalEntityParty | null | undefined): boolean => {
  if (!entity?.commonName) return false

  const codeEntry = entity?.legalEntityCodes?.find(
    (c) => c.legalEntityCodeType === 'ABN' || c.legalEntityCodeType === 'ACN'
  )
  const codeData = codeEntry?.legalEntityCodeData
  const codeType = codeEntry?.legalEntityCodeType

  // Remove spaces before checking length
  const codeDigitsOnly = codeData?.replace(/\s/g, '')
  
  // ABN is 11 digits, ACN is 9 digits
  const isCodeValid = 
    (codeType === 'ABN' && codeDigitsOnly?.length === 11) ||
    (codeType === 'ACN' && codeDigitsOnly?.length === 9)
  
  // Default onboarded to false if not provided (undefined/null)
  const isOnboarded = entity?.onboarded ?? false

  return isCodeValid && !isOnboarded
}

export const EntityDetailsRow = forwardRef(
  (
    {
      label,
      type,
      touched,
      errors,
      getFieldProps,
      setValue,
      initialOption,
      handleClear,
      initiatedBy,
      initialValues,
      resetTermsRef,
      resetIssuer,
      issuerRef,
      setPOC,
      disabled,
      singleEntry,
      isOnbordedRequired,
      onTrusteeNameChange
    }: EntityProps,
    ref
  ) => {
    const dispatch = useDispatch()
    const { entities } = useSelector((state: RootState) => state?.legalEntities)
    const { issuers } = useSelector((state: RootState) => state?.termsReducer)
    const { onSocketSend } = useSocketConnection()
    const [orgs, setOrgsList] = useState<LegalEntityParty[]>([])
    const [labelEntered, setLabelEntered] = useState<string>('')
    const [optionSelected, setOption] = useState<LegalEntityParty>(
      initialOption || legalEntityBaseValue
    )
    const [isSelected, setIsSelected] = useState(false)
    const { applicants, beneficiaries, id, guaranteeType } = useContext(GlobalInfoContext)
    // Get designatedPOC from context or from the selected option (for Amend mode)
    const designatedPOC = 
      beneficiaries?.[0]?.designatedPointOfContact || 
      (optionSelected as any)?.designatedPointOfContact
    const [open, setOpen] = useState(false)

    const [showPocForm, setShowPocForm] = useState(false)
    const [poc, setPoc] = useState<PointOfContact | null>(null)
    const pocFirstNameRef = useRef<HTMLInputElement | null>(null)
    
    // Trustee name state for trust beneficiaries
    const [trusteeName, setTrusteeName] = useState<string>(getTrusteeName(initialOption))
    const [trusteeNameError, setTrusteeNameError] = useState<string>('')

    // Memoized check for whether the POC section should be displayed at all
    // This ensures render condition and state logic use the exact same criteria
    const shouldShowPocSection = useMemo(() => {
      if (type !== 'beneficiaries') return false
      return shouldShowPocForEntity(optionSelected)
    }, [type, optionSelected])
    
    // Check if this beneficiary entity needs a trustee name
    const needsTrusteeName = useMemo(() => {
      if (type !== 'beneficiaries') return false
      return entityNeedsTrusteeName(optionSelected)
    }, [type, optionSelected])

    useImperativeHandle(ref, () => ({
      resetOptions() {
        setOption(legalEntityBaseValue)
        setOpen(false)
        setShowPocForm(false)
        setPoc(null)
        setTrusteeName('')
        setTrusteeNameError('')
      }
    }))

    useImperativeHandle(issuerRef, () => ({
      resetIssuerOptions() {
        if (type === 'issuers') {
          setOption(legalEntityBaseValue)
          setOpen(false)
        }
      }
    }))

    useEffect(() => {
      if (!Array.isArray(initialValues) || initialValues.length !== 1) return
      let leEntity: LegalEntityParty | null = null
      if (type === 'issuers') {
        leEntity = initialValues[0]?.issuerLegalEntity || null
      } else if (type === 'applicants') {
        leEntity = initialValues[0]?.businessUnitActionContext?.[0]?.legalEntity || null
      }
      if (
        leEntity &&
        leEntity.legalEntityIdentifier &&
        leEntity.legalEntityIdentifier !== optionSelected?.legalEntityIdentifier
      ) {
        setOption(leEntity)
        setValue(leEntity)
      }
    }, [type, initialValues, optionSelected?.legalEntityIdentifier])

    // Restore selected applicant/issuer from context when there are multiple available options
    useEffect(() => {
      if (type === 'applicants' && Array.isArray(initialValues) && initialValues.length > 1) {
        if (applicants && applicants.length === 1 && applicants[0]?.legalEntityIdentifier) {
          const contextApplicant = applicants[0]
          if (contextApplicant.legalEntityIdentifier !== optionSelected?.legalEntityIdentifier) {
            setOption(contextApplicant)
            setValue(contextApplicant)
          }
        }
      } else if (type === 'issuers' && Array.isArray(initialValues) && initialValues.length > 1) {
        if (issuers && issuers.length === 1 && issuers[0]?.legalEntityIdentifier) {
          const contextIssuer = issuers[0]
          if (contextIssuer.legalEntityIdentifier !== optionSelected?.legalEntityIdentifier) {
            setOption(contextIssuer)
            setValue(contextIssuer)
          }
        }
      }
    }, [type, initialValues, applicants, issuers, optionSelected?.legalEntityIdentifier])

    useEffect(() => {
      if (type === 'applicants') {
        const applicantEntity = initialValues.map(
          (i) => i?.businessUnitActionContext?.[0]?.legalEntity
        )
        if (applicantEntity) {
          setOrgsList([...applicantEntity])
        }
      } else if (type === 'issuers') {
        const issuerEntity = initialValues?.map((o) => o.issuerLegalEntity)
        if (issuerEntity) {
          setOrgsList([...issuerEntity])
        }
      }
    }, [initialValues, type])

    // Update showPocForm when optionSelected changes and it's a beneficiary
    useEffect(() => {
      if (type === 'beneficiaries') {
        const shouldShow = shouldShowPocForEntity(optionSelected)
        setShowPocForm(shouldShow)
      }
    }, [type, optionSelected])

    // Restore POC from context for beneficiaries - only on initial load
    useEffect(() => {
      if (type !== 'beneficiaries') return
      
      // If POC section should not show, clear POC data
      if (!shouldShowPocSection) {
        setPoc(null)
        return
      }
      
      // Try to get POC from multiple sources
      const contextPOC = beneficiaries?.[0]?.designatedPointOfContact
      const optionPOC = (optionSelected as any)?.designatedPointOfContact
      const pocData = contextPOC || optionPOC
      
      if (pocData?.person?.firstName && pocData?.person?.lastName) {
        const { person, position } = pocData
        // Only restore POC if we don't already have one set locally
        if (!poc?.person?.firstName) {
          setPoc({ person, position })
          // If we have a complete POC already, show summary instead of form
          setShowPocForm(false)
        }
      } else {
        // No POC data - show the editable form
        setShowPocForm(true)
      }
    }, [beneficiaries, type, shouldShowPocSection, optionSelected])

    // Focus POC first name field when form is shown
    useEffect(() => {
      if (type === 'beneficiaries' && showPocForm && pocFirstNameRef.current) {
        setTimeout(() => {
          pocFirstNameRef.current?.focus()
        }, 50)
      }
    }, [showPocForm, type])

    // Handler for when entity selection changes - used by child components
    const handleEntitySelected = (entity: LegalEntityParty) => {
      setOption(entity)
      setValue(entity)
      // Reset POC and trustee name when entity changes
      if (type === 'beneficiaries') {
        setPoc(null)
        setPOC?.(undefined)
        // Reset trustee name when entity changes - check both requestedTrustee and handlingInstructions
        setTrusteeName(getTrusteeName(entity))
        setTrusteeNameError('')
        // showPocForm will be updated by the useEffect watching optionSelected
      }
    }
    
    // Handler for trustee name changes
    const handleTrusteeNameChange = (name: string) => {
      setTrusteeName(name)
      setTrusteeNameError('')
      
      // Update the entity with the trustee name using requestedTrustee structure
      // Preserve all existing properties including onboarded status
      const updatedEntity: LegalEntityParty = {
        ...optionSelected,
        onboarded: optionSelected?.onboarded,
        designatedPointOfContact: optionSelected?.designatedPointOfContact,
        requestedTrustee: { trusteeName: name }
      }
      setValue(updatedEntity)
      
      // Notify parent component
      onTrusteeNameChange?.(name)
    }

    // Determine business ID validation error (used for BusinessIdAutoComplete helper)
    const businessIdError = (() => {
      if (type !== 'beneficiaries') return ''
      const idError = (errors as any)?.commonName || (errors as any)?.legalEntityIdentifier
      const idTouched = (touched as any)?.commonName || (touched as any)?.legalEntityIdentifier
      return idTouched && idError ? idError : ''
    })()

    return (
      <>
        <Grid item xs={5.5} sm={5} md={5} lg={4}>
          <Item>
            <Box>
              {(type === 'applicants' && orgs.length === 1) ||
              (type === 'issuers' && orgs.length === 1) ? (
                <FauxTextField
                  label={label}
                  value={
                    type === 'applicants'
                      ? initialValues?.[0]?.businessUnitActionContext?.[0]?.legalEntity
                          ?.commonName ?? ''
                      : orgs?.[0]?.commonName ?? ''
                  }
                  singleEntry
                  showSuccess
                />
              ) : type !== 'beneficiaries' ? (
                <Autocomplete
                  disablePortal
                  id={`${type}`}
                  options={orgs}
                  sx={{ minWidth: 180 }}
                  isOptionEqualToValue={(option, value) => {
                    if (!value?.legalEntityIdentifier) return false
                    return option?.legalEntityIdentifier === value?.legalEntityIdentifier
                  }}
                  getOptionLabel={(options: LegalEntityParty) => options?.commonName || ''}
                  onInputChange={(event, value, reason) => {
                    if (reason === 'clear') {
                      setOption(legalEntityBaseValue)
                      setLabelEntered('')
                      handleClear(type)
                      resetTermsRef?.current?.resetTerms()
                      resetIssuer?.current?.resetIssuerOptions()
                      dispatch(clearAllLegalEntites())
                      dispatch(clearAllTermsConditions())
                      setIsSelected(false)
                    } else {
                      setLabelEntered(value)
                    }
                  }}
                  onChange={(event, option, reason) => {
                    if (reason === 'selectOption' && option) {
                      handleEntitySelected(option)
                      setIsSelected(true)
                    }
                  }}
                  value={optionSelected}
                  onBlur={() => {
                    setOpen(false)
                    dispatch(clearAllLegalEntites())
                  }}
                  disableClearable
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={label}
                      sx={{ width: '98%' }}
                      placeholder="Please select..."
                      autoComplete="off"
                      InputLabelProps={{ shrink: true }}
                      {...getFieldProps?.(`${type}`)}
                      error={Boolean(touched?.[`${type}`] && errors?.[`${type}`])}
                      helperText={touched?.[`${type}`] && errors?.[`${type}`]}
                    />
                  )}
                />
              ) : optionSelected?.commonName ? (
                <FauxTextField
                  label={`${label}`}
                  value={getBeneficiaryDisplayName(optionSelected)}
                  singleEntry
                  showSuccess
                />
              ) : null}
            </Box>
          </Item>
          
          {/* Preview info box - shown in left column parallel to Trustee Name field */}
          {type === 'beneficiaries' && optionSelected?.commonName && entityNeedsTrusteeName(optionSelected) && (
            <Box 
              sx={{ 
                mt: 2, 
                p: 1.5, 
                backgroundColor: '#EDE7DE',
                borderLeft: '3px solid',
                borderColor: '#84ADAE',
                borderRadius: 1,
                width: '98%'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 0.5 }}>
                <InfoOutlinedIcon sx={{ fontSize: 18, color: '#84ADAE', flexShrink: 0, mt: 0.25 }} />
                <Typography variant="caption" sx={{ color: '#84ADAE', fontWeight: 600 }}>
                  Beneficiary will be recorded on the guarantee as:
                </Typography>
              </Box>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: 'text.primary', 
                  fontWeight: 600, 
                  pl: 3, 
                  textTransform: 'uppercase',
                  wordBreak: 'break-word'
                }}
              >
                {getBeneficiaryDisplayName({ ...optionSelected, requestedTrustee: { trusteeName } })}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', pl: 3, mt: 0.5, display: 'block' }}>
                Example Format: Trustee Name (AS THE TRUSTEE FOR) + Trust Name
              </Typography>
            </Box>
          )}
        </Grid>

        <Grid item xs={1} sm={1} md={1} lg={1} />

        <Grid item xs={5.5} sm={5} md={5} lg={4}>
          <Item>
            <BusinessIdAutoComplete
              optionSelected={optionSelected}
              setLabelEntered={setLabelEntered}
              setOption={setOption}
              setValue={(val) => {
                handleEntitySelected(val)
              }}
              label={label.split(' ')[0]}
              type={type}
              initialValues={initialValues}
              isSelected={isSelected}
              clearable={false}
              resetIssuer={resetIssuer}
              resetTermsRef={resetTermsRef}
              handleClear={handleClear}
              setShowPointOfContact={(show: boolean) => {
                // Only used by BusinessIdAutoComplete to explicitly control POC visibility
                if (type === 'beneficiaries') {
                  setShowPocForm(show)
                }
              }}
              setPOC={setPoc}
              error={Boolean(businessIdError)}
              helperText={businessIdError}
              singleEntry={singleEntry}
              disabled={disabled}
            />
          </Item>
          
          {/* Trustee Name input field for trust beneficiaries */}
          {type === 'beneficiaries' && optionSelected?.commonName && (
            <TrusteeNameEditor
              entity={optionSelected}
              trusteeName={trusteeName}
              onTrusteeNameChange={handleTrusteeNameChange}
              disabled={disabled}
              error={trusteeNameError}
            />
          )}
        </Grid>

        {/* POC Section - only render when shouldShowPocSection is true */}
        {shouldShowPocSection && (
          <>
            {disabled ? (
              // Read-only view for Amend mode
              <PointOfContactSummary
                data={poc}
                disabled
              />
            ) : showPocForm ? (
              <PointOfContactEditor
                data={poc}
                refObj={{ firstName: pocFirstNameRef }}
                required={!isOnbordedRequired}
                onSubmit={(data) => {
                  setPoc(data)
                  setPOC?.(data)
                  setShowPocForm(false)
                }}
                onClear={() => {
                  setPoc(null)
                  setPOC?.(undefined)
                  setShowPocForm(true)
                }}
              />
            ) : (
              <PointOfContactSummary
                data={poc}
                onEdit={() => {
                  setShowPocForm(true)
                }}
                onClear={() => {
                  setPoc(null)
                  setPOC?.(undefined)
                  setShowPocForm(true)
                }}
              />
            )}
          </>
        )}
      </>
    )
  }
)

export const FauxTextField = ({
  label,
  value,
  singleEntry,
  showSuccess = false
}: {
  label: string
  value: string
  disabled?: boolean
  singleEntry?: boolean
  showSuccess?: boolean
}) => (
  <Box
    sx={{
      width: '98%',
      border: '1px solid rgba(0, 0, 0, 0.23)',
      borderRadius: '8px',
      padding: '0 14px',
      minHeight: '56px',
      display: 'flex',
      alignItems: 'center',
      fontSize: '16px',
      fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
      lineHeight: '1.4375em',
      color: '#000',
      backgroundColor: '#f0f4f8',
      position: 'relative',
      justifyContent: 'space-between',
      marginTop: '1px',
      boxSizing: 'border-box',
      overflow: 'visible'
    }}
  >
    <Box
      sx={{
        position: 'absolute',
        top: '-8px',
        left: '12px',
        fontSize: '12px',
        padding: '0 4px',
        backgroundColor: '#f0f4f8',
        color: 'rgba(0, 0, 0, 0.6)',
        lineHeight: 1
      }}
    >
      {label}
    </Box>
    <Box 
      sx={{ 
        flexGrow: 1, 
        wordBreak: 'break-word',
        whiteSpace: 'normal',
        paddingRight: '8px',
        fontSize: '16px'
      }}
      title={value}
    >
      {value}
    </Box>
    {singleEntry && (
      <Box sx={{ marginLeft: '8px', marginRight: '4px', flexShrink: 0 }}>
        <LockOutlinedIcon sx={{ color: '#9e9e9e' }} />
      </Box>
    )}
    {showSuccess && (
      <Box sx={{ marginLeft: '8px', marginRight: '4px', flexShrink: 0 }}>
        <CheckCircleIcon color="success" />
      </Box>
    )}
  </Box>
)
